/**
 * 타임라인 기능 설정 인터페이스
 * 타임라인 게시글 작성 시 사용할 기능들을 관리합니다.
 */
export interface ITimelineSetting {
  // 뽑기 아이템 사용
  useLuckyDraw: boolean;
  // 아이템 조합 사용
  useItemCraft: boolean;
  // 조사/탐색 사용
  useInvestigation: boolean;
  // 조사 결과를 아이템으로 수령 (useInvestigation이 true일 때만 유효)
  useInvestigationItemReward: boolean;
  // 민감정보 표시 사용
  useSensitiveContents: boolean;
  // 긴 이미지 접기 사용
  useCollapseImg: boolean;
  // 긴 이미지 접기 기준 높이 (px)
  collapseImageHeight: number;
  // YouTube URL 사용
  useYoutubeURL: boolean;
  // 이미지 업로드 최대 갯수 (1~10)
  maxImageCount: number;
  // 이미지 필수 작성 (손님/멤버만 해당, 관리자 제외)
  requireImage: boolean;
  // 공개범위 설정 사용 (off시 전체공개로 고정)
  useAccessType: boolean;
  // 해시태그 사용
  useHashtag: boolean;
  // 직접 그리기(드로잉툴) 사용
  useDrawingTool: boolean;
  // 선물하기 사용
  useGift: boolean;
}

/**
 * 뽑기·조사 결과 카드 디자인 설정
 */
export type BorderImageRepeat = 'stretch' | 'round' | 'repeat';

export interface IResultCardDesign {
  borderImageURL?: string;
  backgroundColor?: string;
  textColor?: string;
  subTextColor?: string;
  borderImageWidth?: number;
  borderImageOutset?: number;
  borderImageRepeat?: BorderImageRepeat;
  borderImageSlice?: number;
  showTitle?: boolean;
}

export interface ITimelineResultDesignSetting {
  investigationCard?: IResultCardDesign;
  luckyDrawCard?: IResultCardDesign;
  giftCard?: IResultCardDesign;
}

export const defaultResultCardDesign: IResultCardDesign = {
  borderImageURL: '',
  backgroundColor: '',
  borderImageWidth: 30,
  borderImageOutset: 0,
  borderImageRepeat: 'stretch',
  borderImageSlice: 30,
};

export const defaultTimelineResultDesignSetting: ITimelineResultDesignSetting = {
  investigationCard: { ...defaultResultCardDesign },
  luckyDrawCard: { ...defaultResultCardDesign },
  giftCard: { ...defaultResultCardDesign },
};

/**
 * 타임라인 댓글 옵션 설정 인터페이스
 */
export interface ITimelineCommentSetting {
  // 이미지 사용
  useImage: boolean;
  // 동영상 사용
  useVideo: boolean;
  // 이모티콘 사용
  useEmoticon: boolean;
  // 비밀글 사용
  useSecret: boolean;
  // 배틀 사용
  useBattle: boolean;
  // 배틀 모드 ('instant' = 즉시결과, 'challenge' = 도전장)
  battleMode: 'instant' | 'challenge';
  // 주사위 사용
  useDice: boolean;
  // 뽑기 사용
  useLuckyDraw: boolean;
  // 댓글접기 사용
  useCollapseComment: boolean;
  // 답글 쓰기 사용
  useReply: boolean;
  // 글자수 표시
  showCharacterCount: boolean;
  // 글자수 제한 (기본값 10000)
  maxCharacterCount: number;
  // 게시글 작성자 배지 표시 여부
  showBoardAuthorBadge: boolean;
  // 게시글 작성자 배지 아이콘 (기본값 ▶)
  boardAuthorBadgeIcon: string;
}

/**
 * 타임라인 설정 기본값
 */
export const defaultTimelineSetting: ITimelineSetting = {
  useLuckyDraw: true,
  useItemCraft: true,
  useInvestigation: true,
  useInvestigationItemReward: true,
  useSensitiveContents: true,
  useCollapseImg: true,
  collapseImageHeight: 600,
  useYoutubeURL: true,
  maxImageCount: 4,
  requireImage: false,
  useAccessType: true,
  useHashtag: true,
  useDrawingTool: false,
  useGift: true,
};

/**
 * 타임라인 댓글 옵션 설정 기본값
 */
export const defaultTimelineCommentSetting: ITimelineCommentSetting = {
  useImage: true,
  useVideo: true,
  useEmoticon: true,
  useSecret: true,
  useBattle: true,
  battleMode: 'instant',
  useDice: true,
  useLuckyDraw: true,
  useCollapseComment: true,
  useReply: true,
  showCharacterCount: false,
  maxCharacterCount: 10000,
  showBoardAuthorBadge: true,
  boardAuthorBadgeIcon: '▶',
};


